import React, { useEffect, useRef, useState } from "react";

interface VUMeterProps {
  audioElement: HTMLAudioElement | null;
  width?: number;
  height?: number;
  sensitivity?: number; // sensitivity control (0.1 - 3.0)
}

const VUMeter = ({
  audioElement,
  width = 420,
  height = 120,
  sensitivity = 1.0,
}: VUMeterProps) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const sourceRef = useRef<MediaElementAudioSourceNode | null>(null);
  const splitterRef = useRef<ChannelSplitterNode | null>(null);
  const leftAnalyserRef = useRef<AnalyserNode | null>(null);
  const rightAnalyserRef = useRef<AnalyserNode | null>(null);
  const animationRef = useRef<number | null>(null);

  const [leftLevel, setLeftLevel] = useState(0);
  const [rightLevel, setRightLevel] = useState(0);
  const [leftPeak, setLeftPeak] = useState(0);
  const [rightPeak, setRightPeak] = useState(0);
  const [corsError, setCorsError] = useState(false);

  // Peak hold timers
  const leftPeakTimer = useRef<NodeJS.Timeout | null>(null);
  const rightPeakTimer = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!audioElement) return;

    const setupAudioAnalysis = async () => {
      try {
        // Create audio context
        audioContextRef.current = new (window.AudioContext ||
          (window as any).webkitAudioContext)();

        // Create source from audio element
        sourceRef.current =
          audioContextRef.current.createMediaElementSource(audioElement);

        // Create channel splitter for stereo analysis
        splitterRef.current = audioContextRef.current.createChannelSplitter(2);

        // Create analysers for left and right channels
        leftAnalyserRef.current = audioContextRef.current.createAnalyser();
        rightAnalyserRef.current = audioContextRef.current.createAnalyser();

        // Configure analysers
        leftAnalyserRef.current.fftSize = 256;
        rightAnalyserRef.current.fftSize = 256;
        leftAnalyserRef.current.smoothingTimeConstant = 0.8;
        rightAnalyserRef.current.smoothingTimeConstant = 0.8;

        // Connect audio nodes
        sourceRef.current.connect(splitterRef.current);
        splitterRef.current.connect(leftAnalyserRef.current, 0);
        splitterRef.current.connect(rightAnalyserRef.current, 1);

        // Connect to destination to maintain audio output
        sourceRef.current.connect(audioContextRef.current.destination);

        startAnalysis();
      } catch (error) {
        console.error("Error setting up audio analysis:", error);
        setCorsError(true);
        // Fall back to simulated VU meter
        startSimulatedAnalysis();
      }
    };

    setupAudioAnalysis();

    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      if (audioContextRef.current) {
        audioContextRef.current.close();
      }
      if (leftPeakTimer.current) clearTimeout(leftPeakTimer.current);
      if (rightPeakTimer.current) clearTimeout(rightPeakTimer.current);
    };
  }, [audioElement]);

  const startSimulatedAnalysis = () => {
    // Fallback: simulate VU meter when CORS prevents real analysis
    const simulate = () => {
      if (!audioElement || audioElement.paused) {
        setLeftLevel(0);
        setRightLevel(0);
        animationRef.current = requestAnimationFrame(simulate);
        return;
      }

      // Simulate realistic audio levels with adjustable sensitivity
      const baseLevel = (0.2 + Math.random() * 0.3) * sensitivity;
      const leftVariation = (Math.random() - 0.5) * 0.2;

      const newLeftLevel = Math.max(0, Math.min(1, baseLevel + leftVariation));
      // For mono simulation, add slight variation to right channel
      const newRightLevel = Math.max(
        0,
        Math.min(1, newLeftLevel + (Math.random() - 0.5) * 0.1),
      );

      setLeftLevel(newLeftLevel);
      setRightLevel(newRightLevel);

      // Update peaks with hold
      if (newLeftLevel > leftPeak) {
        setLeftPeak(newLeftLevel);
        if (leftPeakTimer.current) clearTimeout(leftPeakTimer.current);
        leftPeakTimer.current = setTimeout(() => setLeftPeak(0), 1500);
      }

      if (newRightLevel > rightPeak) {
        setRightPeak(newRightLevel);
        if (rightPeakTimer.current) clearTimeout(rightPeakTimer.current);
        rightPeakTimer.current = setTimeout(() => setRightPeak(0), 1500);
      }

      animationRef.current = requestAnimationFrame(simulate);
    };

    simulate();
  };

  const startAnalysis = () => {
    if (!leftAnalyserRef.current || !rightAnalyserRef.current) return;

    const leftDataArray = new Uint8Array(
      leftAnalyserRef.current.frequencyBinCount,
    );
    const rightDataArray = new Uint8Array(
      rightAnalyserRef.current.frequencyBinCount,
    );

    const analyze = () => {
      if (!leftAnalyserRef.current || !rightAnalyserRef.current) return;

      leftAnalyserRef.current.getByteFrequencyData(leftDataArray);
      rightAnalyserRef.current.getByteFrequencyData(rightDataArray);

      // Calculate RMS levels with adjustable sensitivity
      const leftRMS =
        Math.sqrt(
          leftDataArray.reduce((sum, value) => sum + value * value, 0) /
            leftDataArray.length,
        ) / 255;

      const rightRMS =
        Math.sqrt(
          rightDataArray.reduce((sum, value) => sum + value * value, 0) /
            rightDataArray.length,
        ) / 255;

      // Apply adjustable sensitivity boost
      const boostFactor = 1.2 * sensitivity; // Reduced base sensitivity
      const boostedLeft = Math.min(1, leftRMS * boostFactor);
      const boostedRight = Math.min(1, rightRMS * boostFactor);

      // Use left channel for both if right channel is silent (mono audio)
      const finalRightLevel = boostedRight > 0.01 ? boostedRight : boostedLeft;

      setLeftLevel(boostedLeft);
      setRightLevel(finalRightLevel);

      // Update peaks with hold
      if (boostedLeft > leftPeak) {
        setLeftPeak(boostedLeft);
        if (leftPeakTimer.current) clearTimeout(leftPeakTimer.current);
        leftPeakTimer.current = setTimeout(() => setLeftPeak(0), 1500);
      }

      if (finalRightLevel > rightPeak) {
        setRightPeak(finalRightLevel);
        if (rightPeakTimer.current) clearTimeout(rightPeakTimer.current);
        rightPeakTimer.current = setTimeout(() => setRightPeak(0), 1500);
      }

      animationRef.current = requestAnimationFrame(analyze);
    };

    analyze();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Clear canvas
    ctx.fillStyle = "#000";
    ctx.fillRect(0, 0, width, height);

    // Draw background
    ctx.fillStyle = "#111";
    ctx.fillRect(10, 10, width - 20, height - 20);

    // Draw meter scales
    drawMeterScale(ctx, 20, 20, width - 40, 35, "LEFT", leftLevel, leftPeak);
    drawMeterScale(ctx, 20, 65, width - 40, 35, "RIGHT", rightLevel, rightPeak);
  }, [leftLevel, rightLevel, leftPeak, rightPeak, width, height]);

  const drawMeterScale = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    w: number,
    h: number,
    label: string,
    level: number,
    peak: number,
  ) => {
    const meterWidth = w - 60;

    // Define scale steps and their dB values with even spacing
    const scaleSteps = [
      { db: -20, position: 0 },
      { db: -15, position: 1 },
      { db: -10, position: 2 },
      { db: -6, position: 3 },
      { db: -4, position: 4 },
      { db: -2, position: 5 },
      { db: 0, position: 6 },
      { db: 1, position: 7 },
      { db: 2, position: 8 },
      { db: 4, position: 9 },
      { db: 6, position: 10 },
      { db: 8, position: 11 },
    ];

    const totalSteps = scaleSteps.length - 1; // 11 steps between 12 markers
    const stepWidth = meterWidth / totalSteps;

    // Draw label
    ctx.fillStyle = "#fff";
    ctx.font = "12px monospace";
    ctx.fillText(label, x, y + 15);

    // Draw scale markings at even intervals
    ctx.font = "8px monospace";
    scaleSteps.forEach((step) => {
      const pixelPos = x + 50 + step.position * stepWidth;
      ctx.fillStyle = "#666";
      ctx.fillText(step.db.toString(), pixelPos - 5, y - 5);
      ctx.fillRect(pixelPos, y + 20, 1, 5);
    });

    // Draw meter segments with even spacing
    for (let i = 0; i < totalSteps; i++) {
      const segmentX = x + 50 + i * stepWidth;
      const segmentLevel = i / totalSteps; // 0 to 1 range

      // Get the dB value for this segment
      const currentDb = scaleSteps[i].db;

      let color;
      let isLit = segmentLevel <= level;

      // Color zones based on dB values matching the original design
      if (currentDb <= -2) {
        color = isLit ? "#00ff00" : "#001100"; // Green zone (-20 to -2 dB)
      } else if (currentDb <= 2) {
        color = isLit ? "#ffff00" : "#111100"; // Yellow zone (0 to 2 dB)
      } else {
        color = isLit ? "#ff0000" : "#110000"; // Red zone (4 to 8 dB)
      }

      ctx.fillStyle = color;
      ctx.fillRect(segmentX, y + 2, stepWidth - 1, 16);

      // Add fluorescent glow effect only for lit segments
      if (isLit) {
        ctx.shadowColor = color;
        ctx.shadowBlur = 3;
        ctx.fillRect(segmentX, y + 2, stepWidth - 1, 16);
        ctx.shadowBlur = 0;
      }
    }

    // Draw peak indicator
    if (peak > 0) {
      const peakX = x + 50 + peak * meterWidth;
      ctx.fillStyle = "#ff0000";
      ctx.shadowColor = "#ff0000";
      ctx.shadowBlur = 5;
      ctx.fillRect(peakX - 1, y + 2, 2, 16);
      ctx.shadowBlur = 0;
    }

    // Draw peak indicators (N, C, M) - these activate at higher levels
    const indicators = ["N", "C", "M"];
    indicators.forEach((indicator, i) => {
      const indicatorX = x + meterWidth + 55 + i * 15;
      // N activates at 85%, C at 90%, M at 95% of full scale
      const threshold = 0.85 + i * 0.05;
      const isActive = level > threshold;

      ctx.fillStyle = isActive ? "#ff0000" : "#330000";
      ctx.fillRect(indicatorX, y + 2, 12, 16);

      ctx.fillStyle = isActive ? "#fff" : "#666";
      ctx.font = "10px monospace";
      ctx.fillText(indicator, indicatorX + 3, y + 13);
    });
  };

  return (
    <div
      style={{
        background: "#364147",
        padding: "10px",
        borderRadius: "5px",
        border: "2px solid #444",
      }}
    >
      <div
        style={{
          color: "#fff",
          fontSize: "16px",
          fontWeight: "500",
          marginBottom: "5px",
          textAlign: "center",
          fontFamily: "monospace",
        }}
      >
        ▼ 12 POINT PEAK PROGRAM METER {corsError && "(SIMULATED)"}
        {/* -
        Sensitivity: {sensitivity}x*/}
      </div>
      <canvas
        ref={canvasRef}
        width={width}
        height={height}
        style={{
          background: "#000",
          border: "1px solid #333",
        }}
      />
    </div>
  );
};

export default VUMeter;
